import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const STEPS = [
  { key: "skills", type: "multi", title: "What are you good at?", options: [
    "coding", "writing", "design", "marketing", "sales", "video", "audio",
    "numbers", "photography", "voice", "fitness", "crafts", "organization",
    "communication", "problem-solving", "ai", "community",
  ]},
  { key: "weekly_hours", type: "single", title: "How many hours a week can you spare?", options: [
    { v: "0-5", l: "0–5 hours" }, { v: "5-10", l: "5–10 hours" },
    { v: "10-20", l: "10–20 hours" }, { v: "20+", l: "20+ hours" },
  ]},
  { key: "income_goal", type: "single", title: "What's your target monthly income?", options: [
    { v: "500", l: "$500/mo — beer money" }, { v: "1000", l: "$1,000/mo — nice cushion" },
    { v: "3000", l: "$3,000/mo — real income" }, { v: "10000", l: "$10,000+/mo — replace day job" },
  ]},
  { key: "experience", type: "single", title: "Any side hustle experience?", options: [
    { v: "beginner", l: "Total beginner" }, { v: "some", l: "Tried a couple things" },
    { v: "experienced", l: "I've made money before" },
  ]},
  { key: "interests", type: "multi", title: "Which vibes appeal to you?", options: [
    "Tech", "Creative", "Writing", "Marketing", "Sales", "Finance",
    "Admin", "Local", "Hospitality", "Community", "Fitness", "Audio",
  ]},
];

export default function Quiz() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ skills: [], interests: [], weekly_hours: "", income_goal: "", experience: "" });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { if (!user) nav("/"); }, [user, nav]);

  const cur = STEPS[step];
  const val = answers[cur.key];
  const canNext = cur.type === "multi" ? val.length > 0 : Boolean(val);

  const toggle = (v) => {
    if (cur.type === "multi") {
      setAnswers({ ...answers, [cur.key]: val.includes(v) ? val.filter(x => x !== v) : [...val, v] });
    } else {
      setAnswers({ ...answers, [cur.key]: v });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quiz", answers);
      await refresh();
      toast.success("Matching you to hustles…");
      nav("/app");
    } catch (e) {
      toast.error("Something went wrong");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen">
      <HeaderNav/>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-20">
        <div className="caption mb-4">Step {step+1} of {STEPS.length}</div>
        <div className="flex gap-1 mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[var(--forest)]" : "bg-[var(--border-warm)]"}`}></div>
          ))}
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-black leading-tight mb-8" data-testid="quiz-question-title">{cur.title}</h1>

        <div className="grid gap-3">
          {cur.options.map((opt) => {
            const v = typeof opt === "string" ? opt : opt.v;
            const l = typeof opt === "string" ? opt : opt.l;
            const selected = cur.type === "multi" ? val.includes(v) : val === v;
            return (
              <button key={v} onClick={()=>toggle(v)}
                data-testid={`quiz-option-${v}`}
                className={`text-left px-6 py-4 rounded-2xl border-2 transition-all font-medium flex items-center justify-between
                  ${selected ? "border-[var(--forest)] bg-[var(--forest)] text-[var(--alabaster)]"
                             : "border-[var(--border-warm)] bg-white hover:border-[var(--forest)]"}`}>
                <span className="capitalize">{l}</span>
                {selected && <Check size={20}/>}
              </button>
            );
          })}
        </div>

        <div className="flex justify-between mt-10">
          <button onClick={()=>setStep(Math.max(0, step-1))} disabled={step===0} className="btn-outline" data-testid="quiz-back-btn">
            <ArrowLeft size={16}/> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={()=>setStep(step+1)} disabled={!canNext} className="btn-primary" data-testid="quiz-next-btn">
              Next <ArrowRight size={16}/>
            </button>
          ) : (
            <button onClick={submit} disabled={!canNext || submitting} className="btn-copper" data-testid="quiz-submit-btn">
              {submitting ? "Matching…" : "See my matches"} <ArrowRight size={16}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
